import { defineStore } from "pinia"
import { useLocalStorage } from "@vueuse/core"
import { ViewScope, type View } from "@/types"
import { OpportunityType } from "@/graphql"
import { deepToRaw } from "@/utils"
import { normalizeViewFilterDefaults } from "@/views/viewFilterDefaults"

const VIEWS_STORAGE_KEY = "views"

// targets can be disabled client side but should be reactivated on refresh
const HUNTING_OPPORTUNITY_VIEW_SCOPES: ViewScope[] = [
	ViewScope.OpportunitiesHuntingMap,
	ViewScope.OpportunitiesHuntingTable,
]

function withOpportunityTargetsRestored(view: View): View {
	if (!HUNTING_OPPORTUNITY_VIEW_SCOPES.includes(view.scope)) return view

	const opportunityTypes = view.filter?.opportunity?.opportunityTypes
	if (!opportunityTypes || opportunityTypes.includes(OpportunityType.OpportunityTarget)) return view

	return {
		...view,
		filter: {
			...view.filter,
			opportunity: {
				...view.filter?.opportunity,
				opportunityTypes: [...opportunityTypes, OpportunityType.OpportunityTarget],
			},
		},
	}
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value)
}

function isView(value: unknown): value is View {
	return isRecord(value) && typeof value.scope === "string"
}

function readViews(value: string): View[] {
	try {
		const storedValue: unknown = JSON.parse(value)
		return Array.isArray(storedValue)
			? storedValue.filter(isView).map(normalizeViewFilterDefaults).map(withOpportunityTargetsRestored)
			: []
	} catch {
		return []
	}
}

function writeViews(views: View[]): string {
	return JSON.stringify(views)
}

function copyView(view: View): View {
	return normalizeViewFilterDefaults(structuredClone(deepToRaw(view)))
}

function copyViews(views: View[]): View[] {
	return structuredClone(deepToRaw(views)).map(normalizeViewFilterDefaults)
}

export const useViewStore = defineStore("view", () => {
	const views = useLocalStorage<View[]>(VIEWS_STORAGE_KEY, [], {
		deep: true,
		flush: "sync",
		listenToStorageChanges: false,
		serializer: {
			read: readViews,
			write: writeViews,
		},
	})

	function replaceViews(nextViews: View[]): void {
		views.value = copyViews(nextViews).map(withOpportunityTargetsRestored)
	}

	function replaceScopedViews(scopes: ViewScope[], nextViews: View[]): void {
		const otherViews = views.value.filter(view => !scopes.includes(view.scope))
		const scopedViews = nextViews.filter(view => scopes.includes(view.scope))
		views.value = [...otherViews, ...copyViews(scopedViews).map(withOpportunityTargetsRestored)]
	}

	function resetViews(): void {
		views.value = []
	}

	function view(scope: ViewScope): View | null {
		return views.value.find(item => item.scope === scope) ?? null
	}

	function upsertView(nextView: View): void {
		const existingIndex = views.value.findIndex(item => item.scope === nextView.scope)
		const view = copyView(nextView)
		if (existingIndex === -1) {
			views.value.push(view)
			return
		}

		views.value[existingIndex] = view
	}

	function patchView(scope: ViewScope, patch: Partial<View>): void {
		const existingView = view(scope)
		upsertView({
			...(existingView ?? { scope }),
			...copyView({ ...patch, scope }),
			scope,
		})
	}

	return {
		views,
		replaceViews,
		replaceScopedViews,
		resetViews,
		view,
		upsertView,
		patchView,
	}
})
